'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { DatePickerRootContext } from '../date-picker/context/date-picker-root.context';
import { CalendarRootContext } from './context/calendar-root.context';
import { useCalendar } from './hooks/use-calendar';
import { calendarStyles } from './styles/calendar-root.styles';
import type { CalendarRootPropsInterface } from './types/calendar-root.types';

export const CalendarRoot = forwardRef<HTMLDivElement, CalendarRootPropsInterface>(
  (props: CalendarRootPropsInterface, forwardedRef) => {
    const datePickerContext = useContext(DatePickerRootContext);
    const { datePicker } = datePickerContext;
    const {
      children,
      'css': cssProp,
      className,
      minDate = datePickerContext.minDate,
      maxDate = datePickerContext.maxDate,
      disabledDates = datePickerContext.disabledDates,
      dateFormat = datePickerContext.dateFormat,
      selected = datePicker?.pendingValue ?? datePicker?.value,
      defaultSelected = datePicker?.pendingValue ?? datePicker?.value,
      'aria-label': ariaLabel = 'Choose date',
      role = 'region',
      onSelectedChange,
      ...rest
    } = props;

    const calendar = useCalendar({
      minDate,
      maxDate,
      disabledDates,
      dateFormat,
      selected,
      defaultSelected,
      onSelectedChange,
      onValueChange: datePicker?.onValueChange,
      ...rest,
    });

    const styles = css(calendarStyles, cssProp);

    return (
      <CalendarRootContext.Provider value={{ calendar }}>
        <div
          ref={forwardedRef}
          data-c11n-component="Calendar.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          aria-label={ariaLabel}
          role={role}
          {...rest}
        >
          {children}
        </div>
      </CalendarRootContext.Provider>
    );
  },
);

CalendarRoot.displayName = 'Calendar.Root';
